/** Minimum blob size before we bother uploading to Whisper */
const MIN_BLOB_BYTES = 1000;
/** Prefer opus-in-webm, fall back to mp4 for Safari */
function pickMimeType() {
    if (typeof MediaRecorder === 'undefined')
        return 'audio/webm';
    if (MediaRecorder.isTypeSupported('audio/webm;codecs=opus'))
        return 'audio/webm;codecs=opus';
    if (MediaRecorder.isTypeSupported('audio/webm'))
        return 'audio/webm';
    return 'audio/mp4';
}
function extensionFor(mimeType) {
    return mimeType.includes('webm') ? 'webm' : 'mp4';
}
/**
 * VoiceRecorder — wraps MediaRecorder + getUserMedia to record a short
 * voice clip and send it to cloud-api's /api/transcribe (Whisper).
 *
 * Important implementation details (discovered by porting from the
 * original gps-tracker.html and fixing a 422 on the first try):
 *
 *   - The backend endpoint expects the form field to be named `audio`,
 *     NOT `file`. Using `file` returns HTTP 422.
 *   - MediaRecorder must be created with an explicit mimeType so the
 *     Blob type matches what Whisper parses. Safari only supports mp4.
 *   - The blob is sent with a filename matching the mimeType extension
 *     (`voice.webm` or `voice.mp4`).
 *   - Blobs smaller than 1 KB are skipped (too short to transcribe).
 *
 * Emits 'voice:start', 'voice:end', 'voice:transcribed', 'voice:error'.
 */
export class VoiceRecorder {
    constructor(_config, _bus) {
        this._config = _config;
        this._bus = _bus;
        this._mediaRecorder = null;
        this._stream = null;
        this._chunks = [];
        this._isRecording = false;
        this._mimeType = 'audio/webm';
    }
    get isRecording() {
        return this._isRecording;
    }
    async start() {
        if (this._isRecording)
            return;
        try {
            this._stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            this._mimeType = pickMimeType();
            this._mediaRecorder = new MediaRecorder(this._stream, { mimeType: this._mimeType });
            this._chunks = [];
            this._mediaRecorder.ondataavailable = (ev) => {
                if (ev.data.size > 0)
                    this._chunks.push(ev.data);
            };
            this._mediaRecorder.start();
            this._isRecording = true;
            this._bus.emit('voice:start');
        }
        catch (err) {
            this._bus.emit('voice:error', {
                message: err instanceof Error ? err.message : String(err),
            });
            throw err;
        }
    }
    async stop() {
        if (!this._isRecording || !this._mediaRecorder)
            return null;
        return new Promise((resolve) => {
            this._mediaRecorder.onstop = () => {
                const blob = new Blob(this._chunks, { type: this._mimeType });
                // Release mic
                if (this._stream) {
                    this._stream.getTracks().forEach((t) => t.stop());
                    this._stream = null;
                }
                this._mediaRecorder = null;
                this._isRecording = false;
                this._bus.emit('voice:end');
                resolve(blob);
            };
            this._mediaRecorder.stop();
        });
    }
    /** Upload the recorded blob to /api/transcribe and return the transcription */
    async transcribe(blob) {
        if (blob.size < MIN_BLOB_BYTES) {
            this._bus.emit('voice:error', {
                message: `Audio too short (${blob.size} bytes)`,
            });
            return '';
        }
        const ext = extensionFor(blob.type || this._mimeType);
        const fd = new FormData();
        // cloud-api's /api/transcribe expects the field to be named `audio`
        // (not `file`) — sending `file` returns HTTP 422.
        fd.append('audio', blob, `voice.${ext}`);
        try {
            const res = await fetch(`${this._config.cloudApiBootstrap}/api/transcribe`, {
                method: 'POST',
                body: fd,
            });
            if (!res.ok) {
                throw new Error(`/api/transcribe HTTP ${res.status}`);
            }
            const d = await res.json();
            const text = typeof d?.text === 'string' ? d.text : '';
            this._bus.emit('voice:transcribed', { text });
            return text;
        }
        catch (err) {
            this._bus.emit('voice:error', {
                message: err instanceof Error ? err.message : String(err),
            });
            throw err;
        }
    }
}
//# sourceMappingURL=VoiceRecorder.js.map